import { useMemo } from "react";

import { useCapabilityContext } from "@/providers/CapabilitiesProvider";
import type { CapabilityFlag } from "@/types/capabilities";

export function useCapabilities() {
  const ctx = useCapabilityContext();
  const normalized = useMemo(() => {
    const next = { ...ctx.capabilities };
    if (next.canUseEmailAI && next.canUseCrmEmailAI === undefined) {
      next.canUseCrmEmailAI = next.canUseEmailAI;
    }
    if (next.canUseTemplateAutomation && next.canUseCrmTemplateAutomation === undefined) {
      next.canUseCrmTemplateAutomation = next.canUseTemplateAutomation;
    }
    if (next.canUseBandsintownAutomation && next.canUseCrmIngestion === undefined) {
      next.canUseCrmIngestion = next.canUseBandsintownAutomation;
    }
    if (next.canUseLeadScoring) {
      if (next.canUseCrmLeadAutomation === undefined) {
        next.canUseCrmLeadAutomation = next.canUseLeadScoring;
      }
      if (next.canUseCrmEventAutomation === undefined) {
        next.canUseCrmEventAutomation = next.canUseLeadScoring;
      }
    }
    return next;
  }, [ctx.capabilities]);
  return useMemo(() => {
    const capabilityProxy = new Proxy(normalized, {
      get(target, prop, receiver) {
        if (typeof prop === "string") {
          if (prop in target) {
            return Reflect.get(target, prop, receiver);
          }
          if (ctx.hasFullAccess) {
            return true;
          }
        }
        return Reflect.get(target, prop, receiver);
      },
      has(target, prop) {
        if (typeof prop === "string" && ctx.hasFullAccess) {
          return true;
        }
        return Reflect.has(target, prop);
      },
    });
    const base = {
      tier: ctx.tier,
      openUpgradeModal: ctx.openUpgradeModal,
      capabilities: capabilityProxy,
      hasFullAccess: ctx.hasFullAccess,
      ready: ctx.ready,
    } as Record<string, unknown>;
    return new Proxy(base, {
      get(target, prop) {
        if (prop === "capabilities") {
          return capabilityProxy;
        }
        if (Reflect.has(target, prop)) {
          return Reflect.get(target, prop);
        }
        if (typeof prop === "string") {
          if (prop in normalized) {
            return normalized[prop];
          }
          if (ctx.hasFullAccess) {
            return true;
          }
        }
        return undefined;
      },
      has(target, prop) {
        if (Reflect.has(target, prop)) {
          return true;
        }
        if (typeof prop === "string" && ctx.hasFullAccess) {
          return true;
        }
        return prop in normalized;
      },
    }) as typeof normalized & {
      capabilities: typeof capabilityProxy;
      tier: string | null;
      openUpgradeModal: (feature?: string) => void;
      hasFullAccess: boolean;
    };
  }, [normalized, ctx.hasFullAccess, ctx.openUpgradeModal, ctx.tier]);
}

export function useHasCapability(flag: CapabilityFlag | string): boolean {
  const { capabilities, hasFullAccess } = useCapabilities();
  if (!flag) return true;
  if (flag in capabilities) {
    return Boolean(capabilities[flag]);
  }
  return hasFullAccess;
}
